/* The survey editor's FORM — KANON `04-edytor.html` and `05-edytor-dodaj-pytanie.html`, from the
 * name block down to the „Nowe pytanie" card.
 *
 * Split out of `pages/reports/ui/SurveyEditor.module.css` on 27 September 2026 when the editor was
 * promoted into this feature for its second host, the public form builder; not one declaration
 * changed in the move. The screen's own layout — the bar, the scrollport, the skeleton — stayed with
 * the screen as `SurveyEditorScreen.module.css`.
 *
 * The CONTROLS are `shared/ui/surveyCanon.module.css`'s. **Nothing below may restate a property that
 * file declares** — a declaration on both sides is settled by chunk order, which differs between the
 * dev server and the build.
 *
 * Every value is a token. No literal colour, spacing, radius, shadow, duration or easing. */

/* ── The name block ────────────────────────────────────────────────────────────────────────── */

.nameBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.label {
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* The canon's own 22 / 800 / -.02em in a 48px inset frame. It was `--fs-h2` (21) at `--ls-name`
 * (-.018em), which is a step and a half off a heading the trainer reads first. */
.nameField {
  box-sizing: border-box;
  width: 100%;
  height: var(--h-report-editor-name);
  padding: var(--space-0) var(--space-14px);
  font-family: var(--font-head);
  font-size: var(--fs-report-name);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-13px);
}

/* A refused field is still a field: it keeps its box and its ink so it reads as a value the trainer
 * HAS rather than as a control that failed to draw. `readonly` rather than `disabled`, so it stays
 * focusable and carries its own explanation — the view ADR §12 rule. */
.nameField[readonly] {
  color: var(--text-2);
  cursor: default;
  background: var(--bg-sunken);
}

.note {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* ── The assigned-clients bar ──────────────────────────────────────────────────────────────── */

/* Twice as tall as it was, and it is the MOBILE app's bar rather than the export's.
 *
 * The owner looked at „JK AT NS · 3 przypisanych klientów · Zarządzaj ›" beside the phone's own on
 * 17 September 2026 and refused it: a 32px band around a 26px disc leaves no room for a face, let
 * alone a photograph of one. `CheckInManageHeader`'s `bar` is the reference — 13/14 around a 32px
 * `checkInStack` disc at radius 16 — and the canon's own 16/18 inset around the same disc floors
 * the box at `--h-report-editor-assigned`. The RADIUS and the edge were already the phone's. */
.assigned {
  display: flex;
  min-height: var(--h-report-editor-assigned);
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-4) var(--space-5);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-16px);
}

/* The intake's own line, where a check-in draws the stack and „Zarządzaj". The box keeps the bar's
 * floor (`min-height` above) so the sentence does not stand in a shorter card than a check-in's. */
.assignedNote {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.assignedCount {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* „Zarządzaj ›" is a LINK in the canon's drawing — a word and a chevron, no box. `base.css` resets
 * only `font-family` and `cursor` on a button, so without these three the user agent's `buttonface`
 * fill drew a grey pill around it in Flame. Seen in the running app, 17 September 2026. */
.manage {
  display: inline-flex;
  flex: none;
  gap: var(--space-1);
  align-items: center;
  padding: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text);
  background: transparent;
  border: 0;
}

.manage:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.manageIcon {
  display: block;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
}

/* ── A section ─────────────────────────────────────────────────────────────────────────────── */

.section {
  display: flex;
  flex-direction: column;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
}

/* The canon raises a section's edge to .3 while the „Nowe pytanie" card is open inside it, which is
 * the only thing saying which section the card belongs to once it is 600px down the column.
 * `:has()` rather than a flag threaded down through two components — the relationship IS "this
 * section contains the open card", and a boolean prop would be a second way of saying it. */
.section:has(.newCard) {
  border-color: var(--border-3);
}

.sectionHead {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-height: var(--h-report-editor-sechead);
  padding: var(--space-14px) var(--space-4);
}

.sectionText {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.sectionTitle {
  margin: var(--space-0);
  font-size: var(--fs-h3);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.sectionHint {
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.sectionCount {
  flex: none;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* The canon draws a rotated chevron at the end of every section head. It is DECORATION here and
 * not a control: none of these sections collapses, and drawing a control that does nothing is
 * worse than drawing the mark the export drew. */
.sectionChevron {
  display: block;
  flex: none;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
  color: var(--text-2);
  transform: rotate(180deg);
}

.sectionBody {
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
  list-style: none;
}

/* ── The cadence rows ──────────────────────────────────────────────────────────────────────── */

/* The canon's `padding:6px 16px` over rows that are `.q`s with their side padding taken off — so
 * the hairlines between them run the full width of the block and the three labels share one 96px
 * column. It was a 16px box of gapped rows with no rules at all. */
.cadence {
  display: flex;
  flex-direction: column;
  padding: var(--space-6px) var(--space-4);
}

.cadenceRow {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-height: var(--h-report-editor-row);
  padding: var(--space-13px) var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
}

.cadenceRow:first-child {
  border-top: 0;
}

/* A row's control with the sentence saying what it still lacks under it, aligned with the control
 * rather than with the card's edge. */
.cadenceControl {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  align-items: flex-start;
  min-width: var(--space-0);
}

.cadenceLabel {
  flex: none;
  width: var(--w-report-editor-label);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.weekdays {
  display: flex;
  gap: var(--space-6px);
}

/* „Start od" — the canon's `.field` at 36px with the calendar glyph INSIDE it, content-width.
 *
 * The native control's own picker indicator is laid over the whole frame at zero opacity rather
 * than suppressed: hiding it outright takes the date picker with it and leaves a field that can
 * only be typed into. This way the frame IS the picker's target, which is also what the canon's
 * drawing implies. */
.dateFrame {
  position: relative;
  width: auto;
  height: var(--h-report-editor-seg);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
}

.dateField {
  width: auto;
  min-width: 0;
  font: inherit;
  color: inherit;
  background: transparent;
  border: 0;
  outline: none;
  font-variant-numeric: inherit;
}

.dateField::-webkit-calendar-picker-indicator {
  position: absolute;
  inset: var(--space-0);
  width: auto;
  height: auto;
  margin: var(--space-0);
  padding: var(--space-0);
  cursor: pointer;
  opacity: 0;
}

.dateFrame:focus-within {
  border-color: var(--border-3);
}

.monthFrame {
  width: var(--w-report-editor-label);
  height: var(--h-report-editor-seg);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
}

.monthField {
  width: 100%;
  min-width: 0;
  font: inherit;
  color: inherit;
  background: transparent;
  border: 0;
  outline: none;
  font-variant-numeric: inherit;
}

.problem {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  color: var(--danger-ink);
}

/* ── A question row ────────────────────────────────────────────────────────────────────────── */

/* The canon's `.q`: 13 16 over a 52px floor, divided by hairlines. It was `8px 16px`. */
.row {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-height: var(--h-report-editor-row);
  padding: var(--space-13px) var(--space-4);
  border-top: var(--border-hairline) solid var(--border);
}

.row:first-child {
  border-top: 0;
}

/* Where a drop would land. The line is the whole feedback — nothing moves until the drop. */
.row[data-edge='top'],
.row[data-edge='bottom'] {
  box-shadow: var(--shadow-drop-ring);
}

/* The carried row, as the canon's `.lift` draws it: a step above the card under a neutral shadow,
 * with its own corners. It was `opacity: .4`, which said „this is on its way out" rather than
 * „this is in your hand" — and a half-transparent row is the one thing a trainer cannot read while
 * they are deciding where to put it. */
.row[data-dragging] {
  background: var(--bg-chip);
  border-radius: var(--radius-12px);
  box-shadow: var(--shadow-lift);
  outline: var(--border-hairline) solid var(--border-canon-edge);
}

.rowText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3px);
  min-width: 0;
}

/* The canon's 14 / 600, with the „wymagane" lock beside it. */
.rowLabel {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  color: var(--text);
}

.rowLabelText {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The predefined question's own mark, before its word (owner, 17 September 2026).
 *
 * TERTIARY ink, and that is deliberate against the September contrast round: that round moved seven
 * pieces of READ TEXT off `--text-3` and kept the ink for „the eyebrows and the option index" — marks
 * beside a label rather than words to be read. This is one of those. It carries no meaning the label
 * does not already carry in words, so it owes no 4.5:1, and colour is not encoding anything here.
 *
 * It steps to `--text-2` with the row under the pointer, which is the only state a row has. */
.rowGlyph {
  display: block;
  flex: none;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
  color: var(--text-3);
}

.row:hover .rowGlyph {
  color: var(--text-2);
}

/* An unticked standard question. `--text-2`, not `--text-3`: this is the LABEL of every row the
 * trainer is deciding about, at `--fs-body`, and the tertiary ink measured 3.01:1 on the card and
 * 2.37:1 on a sunken one — under 4.5:1 in both themes, and not large text at any size on this row. */
.rowOff {
  color: var(--text-2);
}

.rowMeta {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  min-width: 0;
}

/* Every question's answer type — „Liczba", „Skala 1–5". The canon's `.tp`: 11 / 600 with a 13px
 * glyph 5px from it. It is read, not glanced at, and at this size the tertiary ink does not reach
 * 4.5:1 in either theme, so the ink stays `--text-2`. */
.typePill {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
}

.typePillIcon {
  display: block;
  flex: none;
  width: var(--sz-report-glyph-sm);
  height: var(--sz-report-glyph-sm);
}

/* „→ Ciało" — a real destination, and EMBER again since 17 September 2026.
 *
 * It was neutralised earlier in the same week on the argument that an intake draws it on three rows
 * at once and that is the accent as a background. The owner looked at the running screen beside the
 * export and reversed it: the canon writes `color:#ff5a1f` on this note in all three of the files
 * that draw it, and „miało być identycznie wizualnie jak w kierunku 1".
 *
 * The token is `--accent-ink`, not the `--accent-text` the export's hex maps to: this is a run of
 * WORDS, and `--accent-text` measures 3.98:1 as words in Flame. `--accent-ink` is the accent-as-words
 * step and clears AA on both grounds. That is the one deviation, and it is a contrast one.
 *
 * Applied BESIDE `.typePill` rather than composed into it: `composes` is not part of this project's
 * stylelint contract, and two class names on one element say the same thing without it. */
.mapNoteInk {
  color: var(--accent-ink);
}

/* ── „+ Dodaj pytanie" and the „Nowe pytanie" card ─────────────────────────────────────────── */

.addRow {
  padding: var(--space-3) var(--space-4) var(--space-4);
}

.newCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-20px) var(--space-4);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-16px);
}

.newHead {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
}

.newTitle {
  margin: var(--space-0);
  font-size: var(--fs-h3);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

/* The canon's own 44px on `--bg-sunken` under the .13 edge — a field INSIDE a card that is already
 * inset, so it sinks a step further rather than matching its ground. */
.textField {
  box-sizing: border-box;
  width: 100%;
  height: var(--h-report-newq-field);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-field);
  font-weight: var(--fw-semibold);
  color: var(--text);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-canon-edge);
  border-radius: var(--radius-13px);
}

/* The answer types, at the canon's own 6px gutter.
 *
 * It was a four-column GRID, and a grid gives every chip a quarter of the row whether the word fits
 * or not: in the shelf's 460px dialog that track is about 100px and „Krótki tekst", „Długi tekst",
 * „Pojedynczy wybór" and „Wielokrotny wybór" were all cut to „Krótki t…". The owner refused it on 17
 * September 2026. A wrapping flex row sizes each chip to its own word and takes another line when a
 * line is full, so no label is ever shortened — which is the only reading compatible with the words
 * being mobile's verbatim `intakeSurvey.answerType.*` and not ours to shorten. */
.typeGrid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
}

/* The canon's own gap here is the chip's 6, not 8. `flex: none` so a chip is exactly as wide as its
 * own word — the row it stands in wraps rather than squeezing. */
.typeChip {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: var(--sz-report-filter);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border-canon-edge);
  border-radius: var(--r-chip);
}

/* The word, WHOLE.
 *
 * This is the collision between two canons rather than a mistake in either: the export's own grid
 * says „Jeden wybór", „Wiele wyborów" and „Nagranie", and this app names the eleven answer types in
 * mobile's verbatim `intakeSurvey.answerType.*`, which spells the same three longer. The words are
 * mobile's — a second spelling is how two surfaces come to disagree about what „Skala" is called —
 * so the ROW gives way and wraps. `nowrap` keeps a name on one line; the wrapping happens between
 * chips, never inside one. */
.typeChipLabel {
  white-space: nowrap;
}

/* The chosen type is marked by its EDGE and its ink, not by an accent fill: eight chips with one
 * filled orange would spend the accent on a setting rather than on an act. */
.typeChip[aria-checked='true'] {
  color: var(--text);
  background: var(--bg-sunken);
  border-color: var(--text);
}

.options {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.optionRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.optionIndex {
  flex: none;
  width: var(--space-4);
  font-size: var(--fs-micro);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.twoUp {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
}

.toggleChip {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  align-self: flex-start;
  height: var(--sz-report-filter);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border-canon-edge);
  border-radius: var(--r-chip);
}

.toggleChip[aria-pressed='true'] {
  color: var(--text);
  background: var(--bg-sunken);
  border-color: var(--border-3);
}

.toggleIcon {
  display: block;
  flex: none;
  width: var(--sz-report-chip-icon);
  height: var(--sz-report-chip-icon);
  color: var(--text-3);
}

/* The lock glyph inside „Wymagane" is the one accent in this card, and only while it is on. */
.toggleChip[aria-pressed='true'] .toggleIcon {
  color: var(--accent-ink);
}

/* The canon's footnote under the two settings — 11.5 over a relaxed line. */
.newNote {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.newFoot {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: flex-end;
  padding-top: var(--space-13px);
  border-top: var(--border-hairline) solid var(--border);
}

/* „Anuluj" is PLAIN TEXT in the canon — no fill, no edge, `--text-2` at 13.5 / 600 in a 42px box.
 * It wore the ghost skin and drew a bordered pill beside the ember one, which reads as two offers
 * of equal weight where the export offers one. */
.newCancel {
  height: var(--h-report-newq-button);
  padding: var(--space-0) var(--space-14px);
  font-family: var(--font-body);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.newCancel:hover {
  color: var(--text);
}

.newCancel:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

/* „Dodaj pytanie" — the ember at the canon's own cut for this card: 42 tall, 0 22, 14px, radius 14,
 * and `box-shadow: none`. The export nulls the ember's shadow HERE specifically, which is the one
 * place in this app a primary draws flat, and that is honoured rather than corrected. */
.newSubmit {
  gap: var(--space-2);
  height: var(--h-report-newq-button);
  padding: var(--space-0) var(--space-22px);
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  letter-spacing: normal;
  border-radius: var(--r-chip);
  box-shadow: none;
}

/* The shelf DIALOG's primary — the same ember, square, at the page header's own 36/r12 cut.
 *
 * Icon-only since 17 September 2026 (owner): „Zapisz zmiany" beside „Anuluj" in a 460px dialog put
 * two words of equal length at the same end of the row, and the canon's own answer to a control with
 * one obvious act is a square. It keeps `.newSubmit`'s flat shadow — this card is the one place in
 * the app a primary draws without one — and states nothing else `emberButton` already declares. */
.dialogSubmit {
  width: var(--sz-report-filter);
  height: var(--sz-report-filter);
  padding: var(--space-0);
  border-radius: var(--radius-12px);
  box-shadow: none;
}

.dialogSubmitIcon {
  display: block;
  width: var(--sz-report-glyph-lg);
  height: var(--sz-report-glyph-lg);
}

/* The shelf half of the card. */
.shelfList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  max-height: var(--h-report-picker-list);
  overflow-y: auto;
}

.shelfPick {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-note);
  color: var(--text);
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--radius-md);
}

.shelfPickLabel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

